/**
 * EmbedCustomLink — renders a custom menu link as an iframe inside the app shell.
 * Route: /embed/:linkId
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ExternalLink } from 'lucide-react';
import api from '../lib/api';

export default function EmbedCustomLink() {
  const { linkId } = useParams();
  const navigate = useNavigate();
  const [link, setLink] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.getMyMenuLinks()
      .then((links) => {
        const l = (links || []).find(x => x.id === linkId);
        if (!l) setError('Link not found or you do not have access');
        else setLink(l);
      })
      .catch((e) => setError(e.message || 'Failed to load'));
  }, [linkId]);

  if (error) {
    return (
      <div className="p-8">
        <Card className="p-8 text-center">
          <p className="text-red-500">{error}</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate('/')}>Back to Dashboard</Button>
        </Card>
      </div>
    );
  }
  if (!link) return <div className="p-8 text-muted-foreground">Loading…</div>;

  const allow = [];
  if (link.allow_camera) allow.push('camera');
  if (link.allow_microphone) allow.push('microphone');

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col" data-testid="embed-custom-link">
      <div className="flex items-center justify-between px-4 py-2 border-b">
        <h2 className="text-sm font-semibold">{link.title}</h2>
        <a href={link.url} target="_blank" rel="noopener noreferrer"
           className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1">
          Open in new tab <ExternalLink className="h-3 w-3" />
        </a>
      </div>
      <iframe
        src={link.url}
        title={link.title}
        className="flex-1 w-full border-0"
        allow={allow.join('; ')}
        sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals"
      />
    </div>
  );
}
